/**
 * The inspector for a selected group: its name, where it sits (parent group), its note, the
 * nodes and groups inside it, and delete (which keeps the nodes: they move to the parent).
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import type { Group as GroupData } from "@protocol/Group";
import type { GroupPatch } from "@protocol/GroupPatch";
import { useApp } from "../../app/context";
import { groupAndDescendants, groupChoices, groupPath, nodesInGroup, subGroups } from "../../shared/analysis";
import { toast } from "../../shared/toast";
import { ArmedButton, Group, NodeRef } from "../../shared/ui";
import { useOpenNode } from "./openNode";

/** Everything about one group. */
export function GroupPanel(props: { id: string }): JSX.Element {
  const { graph } = useApp();
  const group = createMemo(() => graph.state.graph.groups.find((g) => g.id === props.id));
  return <Show when={group()}>{(g) => <GroupDetails group={g} />}</Show>;
}

/** The sections of the group inspector, for a group that exists. */
function GroupDetails(props: { group: () => GroupData }): JSX.Element {
  const g = props.group;
  const { graph, actions, view } = useApp();
  const open = useOpenNode();
  const all = () => graph.state.graph;
  /** Sends a change of this group to the core. */
  const update = (patch: GroupPatch) => actions.updateGroup(g().id, patch);
  const members = createMemo(() => nodesInGroup(all(), g().id));
  const inside = createMemo(() => subGroups(all(), g().id));
  const total = createMemo(() => nodesInGroup(all(), g().id, true).length);
  /** Groups this one may move into: any group that is not itself or inside it. */
  const parents = createMemo(() => groupChoices(all(), groupAndDescendants(all(), g().id)));

  return (
    <>
      <div class="ihead">
        <p class="crumbs muted" data-group-path>
          <For each={groupPath(all(), g().id).slice(0, -1)}>
            {(p) => (
              <button type="button" class="linklike" onClick={() => view.selectGroup(p.id)}>
                {p.name} ›
              </button>
            )}
          </For>
        </p>
        <h2 data-group-title>{g().name}</h2>
        <div class="tags">
          <span class="tag">Group</span>
          <span class="tag">
            {total()} {total() === 1 ? "node" : "nodes"}
          </span>
          <span class="tag mono muted" title="Id, used by the nodes in it">
            {g().id}
          </span>
        </div>
      </div>
      <section class="grp">
        <h3>Name</h3>
        <input
          class="field"
          id="group-name"
          maxlength="60"
          autocomplete="off"
          value={g().name}
          onChange={(e) => {
            const name = e.currentTarget.value.trim();
            if (name && name !== g().name) void update({ name });
            else e.currentTarget.value = g().name;
          }}
        />
      </section>
      <section class="grp">
        <h3>Inside</h3>
        <select class="select" id="group-parent" aria-label="Parent group" onChange={(e) => void update({ parent: e.currentTarget.value })}>
          <option value="" selected={!g().parent}>
            Top level
          </option>
          <For each={parents()}>
            {(c) => (
              <option value={c.id} selected={c.id === g().parent}>
                {c.label}
              </option>
            )}
          </For>
        </select>
      </section>
      <section class="grp">
        <h3>Description</h3>
        <input
          class="field"
          id="group-note"
          maxlength="300"
          autocomplete="off"
          placeholder="What the nodes in this group have in common"
          value={g().note}
          onChange={(e) => {
            const note = e.currentTarget.value.trim();
            if (note !== g().note) void update({ note });
          }}
        />
      </section>
      <Group title="Groups inside" count={inside().length}>
        <For each={inside()}>
          {(sub) => (
            <li>
              <button type="button" class="ref" data-go-group={sub.id} onClick={() => view.selectGroup(sub.id)}>
                <span>{sub.name}</span>
                <em>{nodesInGroup(all(), sub.id, true).length} nodes</em>
              </button>
            </li>
          )}
        </For>
      </Group>
      <Group title="Nodes" count={members().length}>
        <For each={members()}>
          {(n) => (
            <NodeRef
              node={n}
              onOpen={() => open(n.id)}
              removeTitle="Take out of the group:"
              onRemoveLink={() => void actions.updateNode(n.id, { group: g().parent ?? "" }).then((ok) => ok && toast(`${n.name} left ${g().name}.`))}
            />
          )}
        </For>
      </Group>
      <div class="btnrow">
        <ArmedButton
          id="delete-group"
          label="Delete group"
          armedLabel={`Click again to delete ${g().name}`}
          onConfirm={() => {
            const name = g().name;
            void actions.removeGroup(g().id).then((ok) => ok && (view.selectGroup(null), toast(`Deleted the group ${name}; its nodes were kept. Press Ctrl+Z to undo.`)));
          }}
        />
      </div>
      <p class="hint2">Drag the group's name or rim on the graph to move all its nodes. Deleting a group keeps its nodes.</p>
    </>
  );
}
